import { useMemo, useState } from "react";

import type { TableRowItem } from "./types";

export type TableSelectionMode = "single" | "multi";

export type TableSelection<ItemType> = {
  mode: TableSelectionMode;
  /** Whether the table shows a header checkbox for the current page. */
  selectAll: boolean;
  keys: string[];
  /**
   * Rows that were selected through this hook. Keys passed in via `value` that
   * were never selected here have no row.
   */
  items: ItemType[];
  getKey: (item: ItemType) => string;
  isSelected: (item: ItemType) => boolean;
  toggle: (item: ItemType, checked: boolean) => void;
  select: (items: ItemType[]) => void;
  deselect: (keys: string[]) => void;
  clear: () => void;
};

type TableSelectionOptions<ItemType> = {
  getKey?: (item: ItemType) => string;
  mode?: TableSelectionMode;
  /** Show the header checkbox in multi mode. Defaults to true. */
  selectAll?: boolean;
  /** Rows selected on mount when the hook owns the selection. */
  initialItems?: ItemType[];
  /**
   * Selected keys owned by the parent, e.g. ids persisted on a record. Omit to
   * let the hook own the selection.
   */
  value?: string[];
  onChange?: (keys: string[]) => void;
};

const defaultGetKey = (item: TableRowItem) => item._id as string;

export const useTableSelection = <ItemType extends TableRowItem>({
  getKey = defaultGetKey,
  mode = "multi",
  selectAll = true,
  initialItems,
  value,
  onChange,
}: TableSelectionOptions<ItemType> = {}): TableSelection<ItemType> => {
  const [internalKeys, setInternalKeys] = useState<string[]>(
    () => initialItems?.map(getKey) ?? [],
  );
  const [seenItems, setSeenItems] = useState<Map<string, ItemType>>(
    () => new Map(initialItems?.map((item) => [getKey(item), item])),
  );

  const keys = value ?? internalKeys;
  const keySet = new Set(keys);
  // Kept referentially stable so consumers can use it as a hook dependency
  const items = useMemo(
    () =>
      keys.flatMap((key) => {
        const item = seenItems.get(key);
        return item ? [item] : [];
      }),
    [keys, seenItems],
  );

  const update = (next: (current: string[]) => string[]) => {
    if (value !== undefined) {
      onChange?.(next(value));
    } else {
      setInternalKeys(next);
    }
  };

  const remember = (toRemember: ItemType[]) => {
    setSeenItems((current) => {
      const updated = new Map(current);
      for (const item of toRemember) {
        updated.set(getKey(item), item);
      }
      return updated;
    });
  };

  const select = (toSelect: ItemType[]) => {
    remember(toSelect);
    const selectedKeys = toSelect.map(getKey);
    if (mode === "single") {
      update(() => selectedKeys.slice(-1));
      return;
    }
    update((current) => [...new Set([...current, ...selectedKeys])]);
  };

  const deselect = (toDeselect: string[]) => {
    const removed = new Set(toDeselect);
    update((current) => current.filter((key) => !removed.has(key)));
  };

  return {
    mode,
    selectAll: mode === "multi" && selectAll,
    keys,
    items,
    getKey,
    isSelected: (item) => keySet.has(getKey(item)),
    toggle: (item, checked) =>
      checked ? select([item]) : deselect([getKey(item)]),
    select,
    deselect,
    clear: () => {
      update(() => []);
      setSeenItems(new Map());
    },
  };
};
